import React from "react";
import styled from "styled-components";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

// #region SCOPED CSS
const StyledCard = styled.div`
    min-height: 222px;
    background: var(--backgrounds-lines-n-400, #1e2631);
    border: 1px solid var(--backgrounds-lines-n-300, #1a222d);
    border-radius: 5px;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    overflow: hidden;

    .card-title {
        min-height: 49px;
        color: var(--text-icons-gray-100, #d1d1d1);
        font-size: 14px;
        font-weight: 500;
        line-height: 17px;
        text-transform: uppercase;
        margin: 0;
        padding: 16px;
        border-bottom: 1px solid var(--backgrounds-lines-n-100, #3c485a);
        box-sizing: border-box;
    }

    .card-content {
        display: flex;
        flex: 1;
        flex-direction: column;
        gap: 11px;
        padding: 24px 16px 16px;
    }

    .metrics-container {
        min-height: 104px;
        background: var(--backgrounds-lines-n-600, #10141b);
        border-radius: 18px;
        padding: 16px 8px;
        display: flex;
        justify-content: space-around;
        align-items: center;
        gap: 8px;
        box-sizing: border-box;
    }

    .metric-column {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        flex: 1;
    }

    .metric-label {
        color: var(--text-icons-gray-100, #d1d1d1);
        font-size: 14px;
        font-weight: 400;
        line-height: 17px;
        text-transform: uppercase;
        margin-bottom: 14px;
    }

    .metric-value {
        color: var(--text-icons-green-500, #0b0);
        font-size: 28px;
        font-weight: 400;
        line-height: 32px;

        &.warning {
            color: var(--text-icons-orange-500, #f90);
        }

        &.danger {
            color: var(--text-icons-red-500, #ff3636);
        }
    }

    .card-footer {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        min-height: 18px;

        &.multiple-actions {
            justify-content: space-between;
        }
    }

    .metric-value-skeleton {
        display: flex;
        align-items: center;
        height: 32px;
        line-height: 0;
    }

    .check-btn {
        min-width: 50px;
        height: 20px;
        background: var(--backgrounds-lines-n-600, #10141b);
        border: 1px solid var(--backgrounds-lines-n-300, #1a222d);
        border-radius: 4px;
        padding: 1px 8px;
        font-size: 10px;
        font-weight: 400;
        line-height: 12px;
        color: var(--text-icons-gray-100, #d1d1d1);
        text-transform: uppercase;
        cursor: pointer;
        outline: none;
        transition:
            color 0.2s,
            background-color 0.2s,
            border-color 0.2s;

        &:hover {
            color: var(--text-icons-gray-100, #d1d1d1);
            background: var(--backgrounds-lines-n-500, #141922);
            border-color: var(--backgrounds-lines-n-100, #3c485a);
        }

        &:active {
            transform: scale(0.97);
        }
    }
`;
// #endregion

export default function DashboardMetricCard({ title, items = [], actions = [], isLoading = false, onAction }) {
    return (
        <SkeletonTheme baseColor="#1E2631" highlightColor="#262E3E" borderRadius="4px">
            <StyledCard className="dashboard-metric-card" aria-busy={isLoading}>
                <h3 className="card-title">{title}</h3>

                <div className="card-content">
                    <div className="metrics-container">
                        {items.map(item => (
                            <div key={item.id} className="metric-column">
                                <span className="metric-label">{item.label}</span>
                                {isLoading ? (
                                    <span className="metric-value-skeleton">
                                        <Skeleton width={44} height={26} />
                                    </span>
                                ) : (
                                    <span className={`metric-value ${item.tone || "success"}`}>{item.value ?? "-"}</span>
                                )}
                            </div>
                        ))}
                    </div>

                    <div className={`card-footer ${actions.length > 1 ? "multiple-actions" : ""}`}>
                        {actions.map(action => (
                            <button
                                key={action.id}
                                type="button"
                                className="check-btn"
                                onClick={() => onAction?.(action)}>
                                {action.label}
                            </button>
                        ))}
                    </div>
                </div>
            </StyledCard>
        </SkeletonTheme>
    );
}
